import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { SPACING } from "../constants/spacing";
import Button from "./Button";

/**
 * Reusable NotFoundContent component for displaying 404 or not found states
 * @param {string} title - The main title (defaults to translated value)
 * @param {string} message - The description message (defaults to translated value)
 * @param {string} primaryButtonText - Text for the primary action button (defaults to translated value)
 * @param {function} onPrimaryClick - Handler for primary button click
 * @param {string} secondaryButtonText - Text for the secondary action button (optional)
 * @param {function} onSecondaryClick - Handler for secondary button click (optional)
 */
const NotFoundContent = ({
  title,
  message,
  primaryButtonText,
  onPrimaryClick = () => window.history.back(),
  secondaryButtonText,
  onSecondaryClick,
}) => {
  const { t } = useTranslation("common");
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "60vh",
        padding: SPACING.lg,
      }}
    >
      <Box
        sx={{
          textAlign: "center",
          maxWidth: "500px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "3rem", md: "4rem" },
              fontWeight: "bold",
              mb: SPACING.lg,
              color: "primary.main",
            }}
          >
            404
          </Typography>
        </Box>

        <Typography
          variant="h2"
          sx={{
            fontWeight: 600,
          }}
        >
          {title || t("notFound.title")}
        </Typography>

        <Typography
          component="p"
          variant="bodyMd"
          sx={{
            color: "text.secondary",
            py: SPACING.sm,
          }}
        >
          {message || t("notFound.message")}
        </Typography>

        <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
          <Button variant="contained" onClick={onPrimaryClick}>
            {primaryButtonText || t("notFound.goBack")}
          </Button>
          {secondaryButtonText && onSecondaryClick && (
            <Button variant="secondary" onClick={onSecondaryClick}>
              {secondaryButtonText}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default NotFoundContent;
